import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../../services/useApi';

interface Perfil {
  id: number;
  descricao: string;
  comandos: string;
  status: boolean;
  olt_id: number;
  caixas_id: number | null;
}

interface PerfisOnuTabProps {
  oltId: number;
}

const PerfisOnuTab: React.FC<PerfisOnuTabProps> = ({ oltId }) => {
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null);
  const [formData, setFormData] = useState({
    descricao: '',
    comandos: '',
    status: true,
    caixas_id: '' as string | number
  });

  useEffect(() => {
    fetchPerfis();
  }, [oltId]);

  const fetchPerfis = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/ftth-olt-perfis-onu', {
        params: { olt_id: oltId }
      });
      const data = response.data.data || response.data;
      setPerfis(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
      setError('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (perfil?: Perfil) => {
    if (perfil) {
      setEditingPerfil(perfil);
      setFormData({
        descricao: perfil.descricao,
        comandos: perfil.comandos,
        status: perfil.status,
        caixas_id: perfil.caixas_id || ''
      });
    } else {
      setEditingPerfil(null);
      setFormData({
        descricao: '',
        comandos: '',
        status: true,
        caixas_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPerfil(null);
  };

  const handleSubmit = async () => {
    if (!formData.descricao || !formData.comandos) {
      toast.error('Preencha a descrição e os comandos');
      return;
    }

    try {
      const payload = {
        ...formData,
        olt_id: oltId,
        caixas_id: formData.caixas_id === '' ? null : formData.caixas_id
      };

      if (editingPerfil) {
        await api.put(`/ftth-olt-perfis-onu/${editingPerfil.id}`, payload);
        toast.success('Perfil atualizado com sucesso!');
      } else {
        await api.post('/ftth-olt-perfis-onu', payload);
        toast.success('Perfil criado com sucesso!');
      }

      handleCloseDialog();
      fetchPerfis();
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar perfil');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deseja realmente excluir este perfil?')) return;

    try {
      await api.delete(`/ftth-olt-perfis-onu/${id}`);
      toast.success('Perfil removido com sucesso!');
      fetchPerfis();
    } catch (error: any) {
      console.error('Erro ao remover perfil:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover perfil');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Perfis de ONU (Custom)</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
        >
          Novo Perfil
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Caixa ID</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {perfis.map((perfil) => (
              <TableRow key={perfil.id}>
                <TableCell>{perfil.descricao}</TableCell>
                <TableCell>
                  <Chip
                    label={perfil.status ? 'Ativo' : 'Inativo'}
                    color={perfil.status ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{perfil.caixas_id || 'Todas'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(perfil)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(perfil.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {perfis.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">Nenhum perfil cadastrado</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingPerfil ? 'Editar Perfil' : 'Novo Perfil'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Descrição (Nome do Perfil)"
              fullWidth
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            />
            <TextField
              label="Comandos (Várias linhas)"
              multiline
              rows={10}
              fullWidth
              placeholder="config\ninterface gpon 1/1/4\nonu {OnuNumero}\n..."
              value={formData.comandos}
              onChange={(e) => setFormData({ ...formData, comandos: e.target.value })}
              helperText="Variáveis: {OltSlot}, {OltPon}, {OnuId}, {ClienteId}, {ClienteNome}, {ContratoId}, {provisionamentoNome}, {GPON_SN}, {VlanId}, {ServicePort1}, {ServicePort2}, {ServicePort3}, {OnuMacAddress}"
            />
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                    color="primary"
                  />
                }
                label={formData.status ? 'Perfil Ativo' : 'Perfil Inativo'}
                sx={{ width: '100%' }}
              />
              <TextField
                label="Caixa ID (Opcional)"
                type="number"
                fullWidth
                size="small"
                value={formData.caixas_id}
                onChange={(e) => setFormData({ ...formData, caixas_id: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerfisOnuTab;
